import React, { useEffect, useState, useCallback } from "react";
import FilterDropdown from "../../components/common/FilterDropdown";
import WorkoutTable from "../../components/ui/WorkoutTable";
import { toast } from "react-toastify";
import {
  getExerciseData,
  getPlansByTag,
  getWorkoutPlanById,
  uploadWorkout,
  deleteWorkoutPlanById
} from "../../api/workout/uploadJson";
import AddNewPlanButton from "./components/AddNewPlanButton";
import { SinglePlanOverViewInput } from "../../config/singleWorkout";
import { useNavigate } from "react-router-dom";
import { PERPAGE_RECORD } from "../../utils/isValidImage";
import CustomPagination from "../../components/common/CustomPagination";

const WorkoutPlans = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [error, setError] = useState(null);
  const [uploadedJson, setUploadedJson] = useState(null);
  const [allPlanData, setAllPlanData] = useState([]);

  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [currentModal, setCurrentModal] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [selectedVideoUrl, setSelectedVideoUrl] = useState("");
  const [pagination, setPagination] = useState(null);

  const navigate = useNavigate();

  const openModal = (modalNumber) => setCurrentModal(modalNumber);
  const filterOptions = ["All", "Featured", "Trending"];
  const fetchPlans = useCallback(async (page = 1) => {
    try {

      let data = []
      if (selectedFilter === "All") {
        data = await getExerciseData({page, limit: PERPAGE_RECORD})
        data.pagination.totalPages = Math.ceil(data.pagination.total / data.pagination.perPage)
        setPagination(data.pagination)
      } else {
        data['data'] = await getPlansByTag(selectedFilter);
      }

      const filteredPlans = data?.data.filter((plan) =>
        searchTerm ? plan?.plan_name?.toLowerCase().includes(searchTerm.toLowerCase()) : plan
      );

      setAllPlanData(filteredPlans)
      setPlans(filteredPlans);
    } catch (error) {
      setError("Failed to fetch plans");
      console.error(error);
    }
  }, [searchTerm, selectedFilter]);

  useEffect(() => {
    const filteredPlans = allPlanData.filter((plan) =>
      searchTerm ? plan?.plan_name?.toLowerCase().includes(searchTerm.toLowerCase()) : plan
    );
    setPlans(filteredPlans);
  },[allPlanData, searchTerm])

  const handlePageChange = (page) => {
    fetchPlans(page).then();
  };

  useEffect(() => {
    fetchPlans().then();
  }, [fetchPlans, selectedFilter]);


  const convertTimestamp = (createdAtSeconds) => {
    const uploadDate = new Date(createdAtSeconds * 1000);
    return uploadDate.toLocaleDateString();
  };

  const tableHeader = [
    { label: "Sr. No", width: "5%" },
    { label: "Workout Details", width: "40%" },
    { label: "Uploaded on", width: "20%" },
    { label: "Status", width: "15%" },
    { label: "Edit", width: "5%" },
    { label: "Action", width: "5%" },
    { label: "Download", width: "10%" },

  ];

  const handleEditClick = async (planId) => {
    setSelectedPlanId(planId);
    try {
      const data = await getWorkoutPlanById(planId);
      setUploadedJson(data);
      openModal(2);
    } catch (error) {
      setError(error.message);
      console.error("Failed to load plan:", error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this workout plan?")) {
      try {
        const response = await deleteWorkoutPlanById(id);
        if (response.message) {
          toast.success("workout plan deleted successfully!");
          await fetchPlans();
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        toast.error("Failed to delete workout plan file.");
        console.error("Error deleting workout plan:", error);
      }
    }
  }

  const showToast = (message, success) => {
    success ? toast.success(message) : toast.error(message);
  };

  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const fileSizeLimit = 50 * 1024 * 1024;
    const validExtensions = ["json"];
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

    if (selectedFile.size > fileSizeLimit) {
      setErrorMessage("File size exceeds 50MB limit.");
      return;
    }

    if (!validExtensions.includes(fileExtension)) {
      setErrorMessage("Invalid file format. Please upload a JSON file.");
      return;
    }

    setErrorMessage(null);
    setFileName(selectedFile.name);
    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const jsonData = JSON.parse(reader.result);

        setUploadedJson(jsonData);
        setSelectedImageUrl(jsonData?.banner_image);
        setSelectedVideoUrl(jsonData?.plan_video);
      } catch (e) {
        setErrorMessage("Invalid JSON file.");
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleImageUrlChange = (url) => {
    setSelectedImageUrl(url);

    setUploadedJson((prevJson) => ({
      ...prevJson,
      banner_image: url,
    }));
  };

  const handleVideoUrlChange = (url) => {
    setSelectedVideoUrl(url);

    setUploadedJson((prevJson) => ({
      ...prevJson,
      plan_video: url,
    }));
  };

  const handleSubmit = async () => {
    if (!file && !uploadedJson?.id) {
      showToast("No file selected for upload.");
      return;
    }

    try {


      let jsonData = { ...uploadedJson };

      if (!jsonData) {
        jsonData = {};
      }

      if (selectedImageUrl) {
        jsonData.banner_image = selectedImageUrl;
      }
      if (selectedVideoUrl) {
        jsonData.plan_video = selectedVideoUrl;
      }

      const updatedFile = new File([JSON.stringify(jsonData)], fileName, {
        type: "application/json",
      });

      const formData = new FormData();
      formData.append("file", updatedFile, fileName);
      const response = await uploadWorkout(formData, fileName);
      if (response) {
        showToast("Workout plan uploaded successfully!", true);
        setFile(null);
        setFileName(null);
        setUploadedJson(null);
        setSelectedImageUrl("");
        setSelectedVideoUrl("");
        navigate("/workout-plans");
      }
    } catch (error) {
      console.error("Error during submission:", error);
      showToast("Failed to upload plan.");
    }
  };
  const handleAddNewPlanClick = () => {
    setSelectedPlanId(null);
  };

  const toggleStatus = async (plan) => {
    // Create a new object instead of modifying directly (React won't detect updates otherwise)
    const updatedPlan = { ...plan, status: !plan.status };

    const updatedFile = new File([JSON.stringify(updatedPlan)], fileName, {
      type: "application/json",
    });

    const formData = new FormData();
    formData.append("file", updatedFile, fileName);
    const response = await uploadWorkout(formData, fileName);
    if (response) {
      showToast("Workout plan uploaded successfully!", true);
      setFile(null);
      setFileName(null);
      setUploadedJson(null);
      setSelectedImageUrl("");
      setSelectedVideoUrl("");
      await fetchPlans()
    }

  };
  const modalAddBtn = "Add New Workout";
  return (
    <div className="right-side-wrapper d-flex overflow-hidden w-full">
      <div className="container py-4 p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-primary">Workout Plans</h2>
          <div className="d-flex gap-2">
            <input
              type="text"
              placeholder="Search plans..."
              className="form-control"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FilterDropdown
              filterOptions={filterOptions}
              onFilterChange={setSelectedFilter}
            />
            <AddNewPlanButton
              setCurrentModal={setCurrentModal}
              currentModal={currentModal}
              openModal={openModal}
              modalAddBtn={modalAddBtn}
              PlanOverViewInput={SinglePlanOverViewInput}
              selectedPlanId={selectedPlanId}
              handleFileUpload={handleFileUpload}
              handleSubmit={handleSubmit}
              uploadedJson={uploadedJson}
              setUploadedJson={setUploadedJson}
              errorMessage={errorMessage}
              fileName={fileName}
              onAddNewPlanClick={handleAddNewPlanClick}
              setSelectedImageUrl={handleImageUrlChange}
              setSelectedVideoUrl={handleVideoUrlChange}
              imageUrl={selectedImageUrl}
              videoUrl={selectedVideoUrl}
            />
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="table-responsive">
          <WorkoutTable
            header={tableHeader}
            plans={plans}
            convertTimestamp={convertTimestamp}
            onEditClick={handleEditClick}
            handleDelete={handleDelete}
            toggleStatus={toggleStatus}
          />
          <CustomPagination pagination={pagination} handlePageChange={handlePageChange} />
        </div>
      </div>
    </div>
  );

};

export default WorkoutPlans;
